import { z } from "zod"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { ChangeEvent, lazy, Suspense, useEffect, useMemo, useState, type KeyboardEvent } from "react"
import { userHasPermission } from "../../../utils"
import { NoPermission } from "../../../components/UI/no-permission"
import TabsList from "../../../components/Common/TabsList"
import { useQuery } from "@tanstack/react-query"
import { FormsNewsletterOrderBy } from "../../../client"
import {
    Box,
    Button,
    Center,
    Flex,
    Icon,
    SkeletonText,
    Spinner,
    Table,
    useDisclosure,
} from "@chakra-ui/react"
import Sort from "../../../components/Common/Sort"
import ActionsMenu from "../../../components/Common/ActionsMenu"
import { PaginationFooter } from "../../../components/Common/PaginationFooter"
import SearchField from "../../../components/Common/SearchField"
import { exportNewsletter, readFormsNewsletters } from "../../../api/newsletters"
import { FiDownload } from "react-icons/fi"
import { Tooltip } from "../../../components/UI/tooltip"
import useCustomToast from "../../../hooks/useCustomToast"

const DetailNewsletter = lazy(() => import("../../../components/Forms/DetailNewsletter"))
const PER_PAGE = 25

const contactSearchSchema = z.object({
  page: z.number().catch(1),
})

export const Route = createFileRoute("/_layout/forms/newsletters")({
    component: Newsletters,
    validateSearch: (search) => contactSearchSchema.parse(search),
})


function NewsletterTable() {
    // Permission check
    const [nl_read, nl_delete] = useMemo(() => {
        return ["read", "delete"].map(
            (action) => userHasPermission([{ perm: `form_newsletter_${action}`, is_allowed: false }])[0].is_allowed
        )
    }, [userHasPermission])

    if(!nl_read) return <NoPermission/>

    const fields = [
        { title: "Name", col: "name", align: undefined },
        { title: "E-Mail", col: "email", align: undefined },
        { title: "Bestätigt", col: "is_confirmed", align: undefined },
        { title: "Erstellungsdatum", col: "created_at", align: undefined },
        { title: "ID", col: null, align: undefined },
        { title: "Aktionen", col: null, align: "right" as const },
    ]

    // Page
    const { page } = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })
    const setPage = (page: number) => navigate({
        search: (prev: { [key: string]: string }) => ({ ...prev, page }),
    })

    // Search
    const [inputValue, setInputValue] = useState<string>("")
    const [searchValue, setSearchValue] = useState<string>("")
    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value)
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            if (inputValue != null) {
            setSearchValue(inputValue)
            }
        }
    }

    const handleOnClick = () => {
        if (inputValue != null) {
            setSearchValue(inputValue)
        }
    }

    const handleClear = () => {
        setInputValue("")
        if (inputValue != null) {
            setSearchValue("")
        }
    }

    // Set page on search value change
    const [prevPage, setPrevPage] = useState<number>(page)
    useEffect(() => {
        if(searchValue == "") {
            setPage(prevPage)
        } else {
            setPrevPage(page)
            setPage(1)
        }
    }, [searchValue])

    // Sorting
    const [columnName, setColumnName] = useState<FormsNewsletterOrderBy>()
    const [orderBy, setOrderBy] = useState<string>("")
    const [isDesc, setIsDesc] = useState<boolean>()

    // Query
    const queryOptions = useMemo(() =>
        readFormsNewsletters({
            per_page: PER_PAGE,
            page,
            order_by: columnName,
            descending: isDesc,
            query: searchValue,
        }),
    [page, columnName, isDesc, searchValue])

    const {
        data: newsletters,
        isPending,
        isPlaceholderData,
    } = useQuery({
        ...queryOptions,
        placeholderData: (prevData) => prevData,
    })

    // Sorting
    const sortTable = (name: FormsNewsletterOrderBy) => {
        if (columnName !== name) {
            setColumnName(name)
            setOrderBy("")
            sorting("")
        } else {
            sorting(orderBy)
        }
    }

    const sorting = (order: string) => {
        switch (order) {
            case "":
                setOrderBy("desc")
                setIsDesc(true)
            break
            case "desc":
                setOrderBy("asc")
                setIsDesc(false)
            break
            case "asc":
                setOrderBy("")
                setIsDesc(undefined)
                setColumnName(undefined)
            break
        }
    }

    // Open forms-contact detail view
    const detailModal = useDisclosure()
    const [newsletterId, setNewsletterId] = useState("")

    const openModal = (id: string) => {
        setNewsletterId(id)
        detailModal.onOpen()
    }

    // Export
    const showToast = useCustomToast()
    const handleExport = () => {
        exportNewsletter(showToast)
    }

    return (
        <>
            <Flex py={8} justify={{base: "flex-start",md: "flex-end"}}>
                <SearchField
                    inputValue={inputValue}
                    onInputChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onClick={handleOnClick}
                    onClear={handleClear}
                />

                <Tooltip showArrow content={`Newsletter exportieren`} openDelay={500}>
                    <Button onClick={handleExport} variant="primary" ml={4} aria-label="Newsletter exportieren">
                        <Icon as={FiDownload} />
                    </Button>
                </Tooltip>
            </Flex>

            {detailModal.open &&
                <Suspense fallback={
                <Center
                    bg="rgba(0, 0, 0, 0.3)"
                    position="fixed"
                    top="0"
                    left="0"
                    width="100vw"
                    height="100vh"
                    zIndex="10000"
                >
                    <Spinner size="lg" color="white" />
                </Center>}
                >
                <DetailNewsletter
                    isOpen={detailModal.open}
                    onClose={detailModal.onClose}
                    newsletterId={newsletterId}
                />
                </Suspense>
            }

            <Table.ScrollArea asChild>
                <Table.Root size={{ base: "sm", md: "md" }} whiteSpace="normal" display={{md: "table"}}>
                <Table.Header>
                    <Table.Row>
                    {fields.map((field, i) =>
                        field.col != null ? (
                        <Table.ColumnHeader
                            key={i}
                            onClick={() => sortTable(field.col as FormsNewsletterOrderBy)}
                            cursor="pointer"
                            textAlign={field.align}
                        >
                            {field.title}
                            {columnName === field.col &&
                            <Sort
                                columnNamePassed={columnName}
                                orderByPassed={orderBy}
                            />
                            }
                        </Table.ColumnHeader>
                        ) : (
                        <Table.ColumnHeader key={field.title} textAlign={field.align}>
                            {field.title}
                        </Table.ColumnHeader>
                        )
                    )}
                    </Table.Row>
                </Table.Header>
                {isPending ? (
                    <Table.Body>
                    <Table.Row>
                        {new Array(6).fill(null).map((_, index) => (
                        <Table.Cell key={index}>
                            <SkeletonText noOfLines={1} paddingBlock="16px" />
                        </Table.Cell>
                        ))}
                    </Table.Row>
                    </Table.Body>
                ) : (
                    <Table.Body>
                    {!newsletters?.data ? (
                        <Table.Row>
                        <Table.Cell colSpan={6} textAlign="center">
                            Keine Newsletter-Einträge verfügbar
                        </Table.Cell>
                        </Table.Row>
                    ) : (
                        newsletters?.data.map((newsletter, i) => (
                        <Table.Row key={i} opacity={isPlaceholderData ? 0.5 : 1}>
                            <Table.Cell onClick={() => openModal(newsletter.id)} cursor="pointer">
                                {newsletter.name}
                            </Table.Cell>

                            <Table.Cell onClick={() => openModal(newsletter.id)} cursor="pointer">
                                {newsletter.email}
                            </Table.Cell>

                            <Table.Cell onClick={() => openModal(newsletter.id)} cursor="pointer">
                                <Flex gap={2}>
                                    <Box
                                        w="2"
                                        h="2"
                                        borderRadius="50%"
                                        bg={newsletter.is_confirmed ? "success" : "danger"}
                                        alignSelf="center"
                                    />
                                    {newsletter.is_confirmed ? "Bestätigt" : "Unbestätigt"}
                                </Flex>
                            </Table.Cell>

                            <Table.Cell onClick={() => openModal(newsletter.id)} cursor="pointer">
                                {new Date(`${newsletter.created_at}`).toLocaleString(
                                    "de-CH",
                                    { timeZone: "Europe/Zurich" },
                                )}
                            </Table.Cell>

                            <Table.Cell onClick={() => openModal(newsletter.id)} cursor="pointer">
                                {newsletter.id}
                            </Table.Cell>

                            <Table.Cell textAlign="right">
                            <ActionsMenu
                                type="Newsletter"
                                value={newsletter}
                                disableDelete={!nl_delete}
                                hideEdit={true}
                            />
                            </Table.Cell>
                        </Table.Row>
                        ))
                    )}
                    </Table.Body>
                )}
                </Table.Root>
            </Table.ScrollArea>
            <PaginationFooter
                onChangePage={setPage}
                page={page}
                count={newsletters?.count ? newsletters?.count : 0}
                perPage={PER_PAGE}
            />
        </>
    )
}

export default NewsletterTable

function Newsletters() {
  const tabs = useMemo(() => [
    { value: "forms", title: "Kontaktanfragen" },
    { value: "forms/subscriptions", title: "Abo-Bestellungen" },
    { value: "forms/newsletters", title: "Newsletter" },
  ], [])

  return <TabsList tabs={tabs} defaultVal="forms" />
}
